// @flow
import * as React from "react"
import classnames from "classnames"
import * as R from "ramda"

import Translate from "../../../../app/scripts/components/Translate"
import { omitRedundantFields } from "../../consts/form"
import TextFieldNote from "../../../booking/scenes/ReservationForm/components/TextField/TextFieldNote"
import { cuckooEvents } from "../../consts/enums"
import cuckoo from "../../services/cuckoo/cuckoo"

type Props = {
  value: ?string,
  onChange: string => void,
  icon?: React.Node,
  label?: React.Node,
  error?: string | Object, // eslint-disable-line flowtype/no-weak-types
  required?: boolean,
  format?: string => string,
  normalize?: string => string,
  twoLines?: boolean,
  touched?: boolean,
  active?: boolean,
  dataTest?: string,
  withoutColon?: boolean, // hack for translations which don't need colon after text
  disableRecording?: boolean,
  type?: "text" | "email" | "number" | "password" | "tel" | "date",
  disabled?: boolean,
  autoComplete?: string,
  corrector?: string => string,
}

type State = {
  isFocused: boolean,
}

export default class NewTextField extends React.PureComponent<Props, State> {
  static defaultProps = {
    format: (value: string) => value,
    type: "text",
  }

  state = {
    isFocused: false,
  }

  handleBlur = () => {
    this.setState({
      isFocused: false,
    })
  }

  handleFocus = () => {
    this.setState({
      isFocused: true,
    })
  }

  handleChange = (event: Event) => {
    const { normalize, onChange, type: inputType } = this.props
    const { target } = event
    if (target instanceof HTMLInputElement) {
      const normalizedValue = normalize ? normalize(target.value) : target.value
      onChange(normalizedValue)

      let currentSelectionIndex = target.selectionStart
      const previousValue = normalizedValue[currentSelectionIndex - 1]
      const currentValue = normalizedValue[currentSelectionIndex]

      if (previousValue === " " || currentValue === " ") {
        currentSelectionIndex += 1
      }

      const wasNormalized = target.value !== normalizedValue

      // There is a bug in some versions of mobile Chrome: When the normalized value is longer
      // than the typed text (eg. creditCardNormalizer transforms "12345" to "1234 5"), in some
      // browsers the cursor isn't placed to the end of the input, but stays on its original
      // position (just before "5" in the card example).
      //
      // This fix has to be in the setTimeout so it is executed after the event that moves the
      // cursor to the wrong position.
      //
      // We don't want to use this if the value was not normalized
      // it makes a weird behaviour if we just add a space inside the value
      if (inputType === "text" && normalize && wasNormalized) {
        window.setTimeout(() => {
          target.setSelectionRange(currentSelectionIndex, currentSelectionIndex)
        }, 0)
      }
    }
  }

  // eslint-disable-next-line class-methods-use-this, flowtype/no-weak-types
  renderError(error: string | Object) {
    // $FlowFixMe
    const props = {
      className: "spField-note spTypo-note",
      ...(typeof error === "string" ? { tKey: error } : error),
    }

    return (
      error && (
        <div className="spField-error">
          <Translate {...props} />
        </div>
      )
    )
  }

  // eslint-disable-next-line class-methods-use-this
  renderLabel(label: ?React.Node, twoLines: ?boolean, required: ?boolean, withoutColon: ?boolean) {
    return (
      <div className="spField-label">
        {label}
        {twoLines || withoutColon ? "" : ":"}
        {required && twoLines ? "*" : ""}
      </div>
    )
  }

  handleSelectingCorrected = () => {
    const { value, corrector } = this.props
    if (value && corrector) {
      const corrected = corrector ? corrector(value) : value

      this.props.onChange(corrected || "")

      cuckoo.infinario("emailCorrected", {
        where: "mmb",
        from: value,
        to: corrected,
      })
      cuckoo.track(cuckooEvents.MANAGE_FORM_INPUT_CORRECTED, {
        from: value,
        to: corrected,
      })
    }
  }

  render() {
    const {
      value,
      format,
      active,
      icon,
      label,
      error,
      touched,
      twoLines,
      required,
      dataTest,
      withoutColon,
      disableRecording,
      disabled,
      type,
      corrector,
    } = this.props

    const classes = classnames("NewTextField", "spField", {
      _error: error && touched,
      _ok: !error && touched,
      _hasLabel: label,
      _oneLine: !twoLines,
      _twoLines: twoLines,
      _active: active || this.state.isFocused,
      _disabled: disabled,
    })

    // Warning: `value` prop on `input` should not be null.
    // Consider using the empty string to clear the component or `undefined` for uncontrolled components.
    const safeValue = value || ""

    return (
      <div className={classes} data-test={dataTest}>
        <label>
          <div className="spField-label-wrap">
            {icon && <div className="spField-icon">{icon}</div>}
            {label && this.renderLabel(label, twoLines, required, withoutColon)}
          </div>

          {error && touched && this.renderError(error)}

          {!error && corrector && (
            <span className="spField-note spTypo-note">
              <TextFieldNote
                handleSelectingCorrected={this.handleSelectingCorrected}
                active={active}
                corrector={corrector}
                value={!active && value && format ? format(safeValue) : safeValue}
                suppressErrorNote
              />
            </span>
          )}

          <div className="spField-input">
            <input
              onBlur={this.handleBlur}
              onFocus={this.handleFocus}
              className={classnames({
                "spField-input-raw": true,
                "fs-hide": disableRecording,
              })}
              {...(disableRecording ? { "data-recording-ignore": "events" } : {})}
              {...R.omit(
                ["twoLines", "normalize", "disableRecording"],
                omitRedundantFields(this.props),
              )}
              value={!active && value && format ? format(safeValue) : safeValue}
              onChange={this.handleChange}
              disabled={disabled}
              type={type}
            />
          </div>
        </label>
      </div>
    )
  }
}
